import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Users, Package, ShoppingCart, DollarSign, Gavel, MessageSquare } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{title}</p>
        <p className={`text-3xl font-bold ${color}`}>{value}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-full ${color} bg-opacity-10`}>
        <Icon size={24} className={color} />
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/analytics/dashboard`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-pulse text-gray-600 dark:text-gray-400 text-xl">Loading analytics...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!analytics) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-red-600 text-xl">Failed to load analytics</div>
        </div>
      </AdminLayout>
    );
  }

  // Prepare chart data for recent orders
  const orderChartData = analytics.recent_orders.reduce((acc, order) => {
    const date = new Date(order.created_at).toLocaleDateString();
    const existing = acc.find(item => item.date === date);
    
    if (existing) {
      existing.orders += 1;
      existing.revenue += order.status === 'paid' ? order.total_amount : 0;
    } else {
      acc.push({
        date,
        orders: 1,
        revenue: order.status === 'paid' ? order.total_amount : 0
      });
    }
    
    return acc;
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400">Welcome to CollectrsInc Admin Panel</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard
            icon={Users}
            title="Total Users"
            value={analytics.users.total}
            subtitle={`${analytics.users.active} active users`}
            color="text-blue-600"
          />
          <StatCard
            icon={Package}
            title="Total Products"
            value={analytics.products.total}
            subtitle={`${analytics.products.featured} featured`}
            color="text-purple-600"
          />
          <StatCard
            icon={ShoppingCart}
            title="Total Orders"
            value={analytics.orders.total}
            subtitle={`${analytics.orders.paid} paid, ${analytics.orders.pending} pending`}
            color="text-green-600"
          />
          <StatCard
            icon={DollarSign}
            title="Total Revenue"
            value={`₹${analytics.revenue.total.toLocaleString()}`}
            subtitle={analytics.revenue.currency}
            color="text-emerald-600"
          />
          <StatCard
            icon={Gavel}
            title="Auctions"
            value={analytics.auctions.total}
            subtitle={`${analytics.auctions.active} active`}
            color="text-orange-600"
          />
          <StatCard
            icon={MessageSquare}
            title="Discussions"
            value={analytics.discussions.total}
            subtitle="Community posts"
            color="text-pink-600"
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Orders Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Orders (Last 7 Days)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={orderChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="orders" stroke="#8884d8" activeDot={{ r: 8 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Revenue Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Revenue (Last 7 Days)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={orderChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="revenue" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Products by Reviews</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b dark:border-gray-700">
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Product</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Category</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Price</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Reviews</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Rating</th>
                </tr>
              </thead>
              <tbody>
                {analytics.top_products.map((product) => (
                  <tr key={product.id} className="border-b dark:border-gray-700">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover rounded" />
                        <span className="font-medium text-gray-900 dark:text-white">{product.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{product.category}</td>
                    <td className="py-3 px-4 text-gray-900 dark:text-white font-medium">₹{product.price}</td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{product.review_count}</td>
                    <td className="py-3 px-4 text-yellow-500 font-medium">{product.rating || 0}⭐</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
